"use client";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Clock, ArrowRight, Send, ArrowUpRight, Loader2, CheckCircle } from "lucide-react";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { MagneticButton } from "@/components/custom/magnetic-button";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect, FormEvent } from "react";
import { staggerContainer, fadeInUp } from "@/lib/animations";
import { X } from "lucide-react";

// JSON-LD Schemas for SEO
const localBusinessJsonLd = {
	"@context": "https://schema.org",
	"@type": "LocalBusiness",
	"@id": "https://thehighrays.com/#organization",
	name: "TheHighrays",
	description: "Digital agency specializing in YouTube promotion, 3D fabrication, and drone services.",
	url: "https://thehighrays.com",
	telephone: ["+91-8273262260", "+1-416-726-4766"],
	email: "info@thehighrays.com",
	address: [
		{
			"@type": "PostalAddress",
			addressLocality: "New Delhi",
			addressRegion: "Delhi",
			addressCountry: "IN",
		},
		{
			"@type": "PostalAddress",
			streetAddress: "994 Fisher Ave",
			addressLocality: "Ottawa",
			addressRegion: "ON",
			postalCode: "K1Z 6P5",
			addressCountry: "CA",
		},
	],
	geo: [
		{
			"@type": "GeoCoordinates",
			latitude: "28.6139",
			longitude: "77.2090",
		},
		{
			"@type": "GeoCoordinates",
			latitude: "45.3778",
			longitude: "-75.7534",
		},
	],
	openingHoursSpecification: {
		"@type": "OpeningHoursSpecification",
		dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
		opens: "09:00",
		closes: "18:00",
	},
	priceRange: "$$",
};

const faqJsonLd = {
	"@context": "https://schema.org",
	"@type": "FAQPage",
	mainEntity: [
		{
			"@type": "Question",
			name: "What is your response time?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "We move fast. Expect a reply within 24 hours. For urgent viral emergencies, call us directly.",
			},
		},
		{
			"@type": "Question",
			name: "Do you work remotely?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "We are digital natives. We work with clients from Tokyo to New York via Zoom, Slack, and other collaboration tools.",
			},
		},
		{
			"@type": "Question",
			name: "How do I start working with TheHighrays?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "Fill out the contact form on our website. Be honest. Be bold. We'll handle the rest.",
			},
		},
		{
			"@type": "Question",
			name: "Do you serve clients globally?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "Our strategies know no borders. We engineer content that transcends language and geography.",
			},
		},
	],
};

export default function ContactPage() {
	const containerRef = useRef(null);
	const { scrollYProgress } = useScroll({
		target: containerRef,
		offset: ["start start", "end end"],
	});

	const x = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);

	// Form state
	const [formData, setFormData] = useState({ name: "", email: "", message: "" });
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);

	// Auto-dismiss notification after 7 seconds
	useEffect(() => {
		if (submitStatus) {
			const timer = setTimeout(() => {
				setSubmitStatus(null);
			}, 7000);
			return () => clearTimeout(timer);
		}
	}, [submitStatus]);

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsSubmitting(true);
		setSubmitStatus(null);

		try {
			const response = await fetch("/api/contact", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(formData),
			});

			const data = await response.json();

			if (response.ok) {
				setSubmitStatus({ type: "success", message: data.message });
				setFormData({ name: "", email: "", message: "" });
			} else {
				setSubmitStatus({ type: "error", message: data.error || "Something went wrong" });
			}
		} catch {
			setSubmitStatus({ type: "error", message: "Failed to send message. Please try again." });
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			{/* JSON-LD Structured Data */}
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify(localBusinessJsonLd),
				}}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify(faqJsonLd),
				}}
			/>
			<div ref={containerRef} className="flex min-h-screen flex-col bg-zinc-950 font-sans text-white selection:bg-red-500 selection:text-white overflow-hidden">

			{/* Hero Section - Split Layout */}
			<section className="relative min-h-screen flex flex-col lg:flex-row pt-20">
				<div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-30 mix-blend-overlay pointer-events-none"></div>

				{/* Left Side - Massive Typography */}
				<div className="lg:w-1/2 p-8 md:p-20 flex flex-col justify-between relative z-10 border-r border-white/5">
					<div>
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8 }}
							className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/5 backdrop-blur-md mb-8"
						>
							<span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
							<span className="font-mono text-xs font-bold uppercase tracking-widest text-zinc-300">Open for Business</span>
						</motion.div>

						<motion.h1
							initial={{ opacity: 0, x: -50 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 1, delay: 0.2 }}
							className="font-syne text-[12vw] lg:text-[10vw] font-bold uppercase leading-[0.8] tracking-tighter text-white mix-blend-difference whitespace-nowrap lg:whitespace-normal"
						>
							Let's <br className="hidden lg:block" /> Talk
						</motion.h1>
					</div>

					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 1, delay: 0.6 }}
						className="mt-12 lg:mt-0"
					>
						<p className="font-inter text-xl text-zinc-400 max-w-md leading-relaxed">
							Ready to engineer your viral moment? We're listening. Tell us about your project, your goals, and your wildest ambitions.
						</p>

						<div className="mt-12 space-y-6">
							{[
								{ label: "Email", value: "info@thehighrays.com", href: "mailto:info@thehighrays.com" },
								{ label: "Phone (CA)", value: "+1 416 726 4766", href: "tel:+14167264766" },
								{ label: "Phone (IN)", value: "+91 8273262260", href: "tel:+918273262260" },
								{ label: "Address (CA)", value: "994 Fisher Ave, Ottawa", href: "https://maps.google.com/?q=994+Fisher+Ave,+Ottawa,+ON+K1Z+6P5" }
							].map((contact, i) => (
								<a key={i} href={contact.href} target={contact.label.includes("Address") ? "_blank" : undefined} rel={contact.label.includes("Address") ? "noopener noreferrer" : undefined} className="block group">
									<div className="text-sm font-mono text-zinc-500 uppercase tracking-widest mb-1">{contact.label}</div>
									<div className="font-syne text-xl md:text-3xl font-bold uppercase text-white group-hover:text-red-500 transition-colors flex items-center gap-2 break-all md:break-normal">
										{contact.value}
										<ArrowUpRight className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity -translate-y-1 translate-x-1 shrink-0" />
									</div>
								</a>
							))}
						</div>
					</motion.div>
				</div>

				{/* Right Side - Minimalist Form */}
				<div className="lg:w-1/2 bg-zinc-900 relative flex items-center justify-center p-8 md:p-20">
					<div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none"></div>
					<div className="absolute top-0 right-0 w-full h-full bg-linear-to-b from-zinc-950/50 to-transparent pointer-events-none"></div>

					<motion.form
						initial={{ opacity: 0, y: 50 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.4 }}
						className="w-full max-w-lg relative z-10 space-y-8"
						onSubmit={handleSubmit}
					>
						<div className="group">
							<label className="mb-3 block font-mono text-xs text-zinc-500 uppercase tracking-widest group-focus-within:text-red-500 transition-colors">
								01. Name
							</label>
							<Input
								type="text"
								required
								placeholder="ENTER YOUR NAME"
								value={formData.name}
								onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
								disabled={isSubmitting}
								className="h-16 w-full rounded-none border-2 border-zinc-600 bg-zinc-900 px-6 font-syne text-xl font-bold uppercase text-white placeholder:text-zinc-500 focus-visible:border-red-500 focus-visible:ring-0 focus-visible:bg-black transition-all duration-300 disabled:opacity-50"
							/>
						</div>
						<div className="group">
							<label className="mb-3 block font-mono text-xs text-zinc-500 uppercase tracking-widest group-focus-within:text-red-500 transition-colors">
								02. Email
							</label>
							<Input
								type="email"
								required
								placeholder="ENTER YOUR EMAIL"
								value={formData.email}
								onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
								disabled={isSubmitting}
								className="h-16 w-full rounded-none border-2 border-zinc-600 bg-zinc-900 px-6 font-syne text-xl font-bold uppercase text-white placeholder:text-zinc-500 focus-visible:border-red-500 focus-visible:ring-0 focus-visible:bg-black transition-all duration-300 disabled:opacity-50"
							/>
						</div>
						<div className="group">
							<label className="mb-3 block font-mono text-xs text-zinc-500 uppercase tracking-widest group-focus-within:text-red-500 transition-colors">
								03. Message
							</label>
							<Textarea
								placeholder="TELL US ABOUT YOUR PROJECT"
								required
								value={formData.message}
								onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
								disabled={isSubmitting}
								className="min-h-40 w-full rounded-none border-2 border-zinc-600 bg-zinc-900 p-6 font-syne text-xl font-bold uppercase text-white placeholder:text-zinc-500 focus-visible:border-red-500 focus-visible:ring-0 focus-visible:bg-black transition-all duration-300 resize-none disabled:opacity-50"
							/>
						</div>

						<AnimatePresence mode="wait">
							{submitStatus && (
								<motion.div
									initial={{ opacity: 0, y: -20, scale: 0.95 }}
									animate={{ opacity: 1, y: 0, scale: 1 }}
									exit={{ opacity: 0, y: -10, scale: 0.95 }}
									transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
									className={`relative overflow-hidden ${
										submitStatus.type === "success"
											? "bg-zinc-900 border-l-4 border-l-green-500"
											: "bg-zinc-900 border-l-4 border-l-red-500"
									}`}
								>
									{/* Progress bar for auto-dismiss */}
									<motion.div
										initial={{ width: "100%" }}
										animate={{ width: "0%" }}
										transition={{ duration: 7, ease: "linear" }}
										className={`absolute bottom-0 left-0 h-0.5 ${
											submitStatus.type === "success" ? "bg-green-500/50" : "bg-red-500/50"
										}`}
									/>
									<div className="p-5 flex items-start justify-between gap-4">
										<div className="flex items-start gap-4">
											<div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center ${
												submitStatus.type === "success" ? "bg-green-500/20" : "bg-red-500/20"
											}`}>
												{submitStatus.type === "success" ? (
													<CheckCircle className="h-5 w-5 text-green-400" />
												) : (
													<X className="h-5 w-5 text-red-400" />
												)}
											</div>
											<div>
												<p className={`font-syne text-sm font-bold uppercase tracking-wide ${
													submitStatus.type === "success" ? "text-green-400" : "text-red-400"
												}`}>
													{submitStatus.type === "success" ? "Message Sent" : "Error"}
												</p>
												<p className="font-inter text-sm text-zinc-400 mt-1">
													{submitStatus.message}
												</p>
											</div>
										</div>
										<button
											onClick={() => setSubmitStatus(null)}
											className="flex-shrink-0 text-zinc-500 hover:text-white transition-colors p-1"
											aria-label="Dismiss notification"
										>
											<X className="h-4 w-4" />
										</button>
									</div>
								</motion.div>
							)}
						</AnimatePresence>

						<div className="pt-4">
							<MagneticButton
								type="submit"
								disabled={isSubmitting}
								className="w-full h-20 bg-white text-black rounded-none border-2 border-white font-syne text-xl font-bold uppercase tracking-wide hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-300 flex items-center justify-center gap-4 group disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{isSubmitting ? (
									<>
										<Loader2 className="h-5 w-5 animate-spin" />
										Sending...
									</>
								) : (
									<>
										Send Message
										<Send className="h-5 w-5 transition-transform group-hover:translate-x-2 group-hover:-translate-y-2" />
									</>
								)}
							</MagneticButton>
						</div>
					</motion.form>
				</div>
			</section>

			{/* Map Section - Stylized Split View */}
			<section className="relative w-full flex flex-col md:flex-row h-[80vh] md:h-[60vh]">
				{/* Delhi Map */}
				<div className="relative w-full md:w-1/2 h-full group overflow-hidden">
					<div className="absolute inset-0 z-10 bg-zinc-900/20 pointer-events-none mix-blend-multiply"></div>
					<iframe
						src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d224345.83922565494!2d77.04417184510023!3d28.52725364301808!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd5b347eb62d%3A0x37205b715389640!2sNew%20Delhi%2C%20Delhi!5e0!3m2!1sen!2sin!4v1718923842733!5m2!1sen!2sin"
						width="100%"
						height="100%"
						style={{ border: 0, filter: "invert(1) grayscale(1) contrast(1.2)" }}
						allowFullScreen={true}
						loading="lazy"
						referrerPolicy="no-referrer-when-downgrade"
						className="w-full h-full opacity-80 group-hover:opacity-100 transition-opacity duration-700"
					></iframe>

					<div className="absolute bottom-10 left-10 z-30 bg-black/80 backdrop-blur-md p-6 border border-white/10 max-w-xs group-hover:border-red-500 transition-colors duration-500">
						<div className="font-mono text-xs text-zinc-500 uppercase tracking-widest mb-2">HQ Location</div>
						<div className="font-syne text-2xl font-bold text-white uppercase">New Delhi, India</div>
						<div className="mt-4 font-inter text-zinc-400 text-sm">
							Strategically positioned to serve clients globally.
						</div>
					</div>
				</div>

				{/* Ottawa Map */}
				<div className="relative w-full md:w-1/2 h-full border-b md:border-b-0 md:border-r border-white/10 group overflow-hidden">
					<div className="absolute inset-0 z-10 bg-zinc-950/20 pointer-events-none mix-blend-multiply"></div>
					<iframe
						src="https://maps.google.com/maps?q=994+Fisher+Ave,+Ottawa,+ON+K1Z+6P5&t=&z=13&ie=UTF8&iwloc=&output=embed"
						width="100%"
						height="100%"
						style={{ border: 0, filter: "invert(1) grayscale(1) contrast(1.2)" }}
						allowFullScreen={true}
						loading="lazy"
						referrerPolicy="no-referrer-when-downgrade"
						className="w-full h-full opacity-80 group-hover:opacity-100 transition-opacity duration-700"
					></iframe>

					<div className="absolute bottom-10 left-10 z-30 bg-black/80 backdrop-blur-md p-6 border border-white/10 max-w-xs group-hover:border-red-500 transition-colors duration-500">
						<div className="font-mono text-xs text-red-500 uppercase tracking-widest mb-2">North America</div>
						<div className="font-syne text-2xl font-bold text-white uppercase">Ottawa, Canada</div>
						<div className="mt-4 font-inter text-zinc-400 text-sm">
							994 Fisher Ave, Ottawa, ON K1Z 6P5
						</div>
					</div>
				</div>
			</section>

			{/* FAQ Section - Minimalist */}
			<section className="py-32 bg-zinc-950 relative">
				<div className="container mx-auto px-4 md:px-10">
					<div className="flex flex-col md:flex-row gap-12">
						<div className="md:w-1/3">
							<h2 className="font-syne text-4xl md:text-5xl font-bold uppercase tracking-tighter text-white sticky top-32">
								Common <br /> <span className="text-zinc-700">Queries</span>
							</h2>
						</div>
						<div className="md:w-2/3">
							<Accordion type="single" collapsible className="w-full space-y-8">
								{[
									{
										q: "Response Time?",
										a: "We move fast. Expect a reply within 24 hours. For urgent viral emergencies, call us directly.",
									},
									{
										q: "Remote Work?",
										a: "We are digital natives. We work with clients from Tokyo to New York via Zoom, Slack, and telepathy (beta).",
									},
									{
										q: "How to Start?",
										a: "Fill out the form above. Be honest. Be bold. We'll handle the rest.",
									},
									{
										q: "Global Reach?",
										a: "Our strategies know no borders. We engineer content that transcends language and geography.",
									},
								].map((faq, i) => (
									<AccordionItem
										key={i}
										value={`item-${i}`}
										className="border-b border-zinc-800 px-0 pb-8"
									>
										<AccordionTrigger className="font-syne text-xl md:text-3xl font-bold uppercase text-zinc-400 hover:text-white hover:no-underline data-[state=open]:text-red-500 transition-colors text-left">
											{faq.q}
										</AccordionTrigger>
										<AccordionContent className="font-inter text-lg text-zinc-500 pt-4">
											{faq.a}
										</AccordionContent>
									</AccordionItem>
								))}
							</Accordion>
						</div>
					</div>
				</div>
			</section>
		</div>
		</>
	);
}
